import { useState } from "react";
import { Plus, Pencil, Trash2, FolderOpen } from "lucide-react";
import { InterviewQaProvider, useInterviewQa } from "./modal/InterviewQaContext";
import CategoryForm from "./ui/CategoryForm";
import type { CategoryCreate, InterviewCategory } from "./api/InterviewQaApi";
import ConfirmModal from "shared/ConfirmModal";

function CategoriesContent() {
  const { categories, categoriesLoading, createCategory, updateCategory, deleteCategory } = useInterviewQa();
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<InterviewCategory | null>(null);
  const [pendingDelete, setPendingDelete] = useState<InterviewCategory | null>(null);
  const [deleting, setDeleting] = useState(false);

  function openAdd() { setEditingCategory(null); setShowForm(true); }
  function openEdit(c: InterviewCategory) { setEditingCategory(c); setShowForm(true); }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteCategory(pendingDelete.id);
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {pendingDelete && (
        <ConfirmModal
          title="Delete category"
          message={`Delete "${pendingDelete.name}"? Questions in this category will still be accessible under "All Questions".`}
          confirmLabel="Delete"
          variant="danger"
          confirming={deleting}
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground font-inter">Interview Categories</h1>
          <p className="text-sm text-muted-foreground font-inter mt-0.5">Manage domain categories for the interview Q&amp;A knowledge base.</p>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium rounded-xl font-inter transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Category
        </button>
      </div>

      {categoriesLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!categoriesLoading && categories.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground border border-dashed border-border rounded-2xl">
          <FolderOpen className="w-10 h-10 mb-3 text-muted-foreground" />
          <p className="font-inter text-sm">No categories yet.</p>
          <button onClick={openAdd} className="mt-2 text-primary hover:text-primary/80 text-sm font-inter underline">
            Create the first category
          </button>
        </div>
      )}

      <div className="grid gap-3">
        {categories.map((cat) => (
          <div
            key={cat.id}
            className="flex items-center gap-4 px-5 py-4 bg-surface/60 border border-border/60 rounded-xl hover:border-border transition-colors"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground font-inter">{cat.name}</p>
              {cat.description && (
                <p className="text-xs text-muted-foreground font-inter mt-0.5 truncate">{cat.description}</p>
              )}
            </div>
            <span className="text-xs font-mono text-muted-foreground bg-surface-muted px-2 py-0.5 rounded-full shrink-0">
              {cat.question_count ?? 0} question{(cat.question_count ?? 0) !== 1 ? "s" : ""}
            </span>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => openEdit(cat)}
                className="p-1.5 text-muted-foreground hover:text-primary hover:bg-surface-muted rounded-lg transition-colors"
                title="Edit"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPendingDelete(cat)}
                className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-surface-muted rounded-lg transition-colors"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <CategoryForm
          initial={editingCategory}
          onSave={editingCategory
            ? (data) => updateCategory(editingCategory.id, data)
            : (data) => createCategory(data as CategoryCreate) // create mode: the form always sends a full payload
          }
          onClose={() => setShowForm(false)}
        />
      )}
    </div>
  );
}

export default function CategoriesPage() {
  return (
    <InterviewQaProvider>
      <CategoriesContent />
    </InterviewQaProvider>
  );
}
